// Commitment list table — FE-041 + FE-043b.
//
// FE-043b layers three things on top of the FE-041 baseline:
//
// 1. **Grouped-by-status sections**. The default render groups rows
//    into one collapsible section per status (Backlog, Ready, In
//    progress, In review, Done — Cancelled only when the URL opts
//    in). Each section header shows the count and toggles open/
//    closed; collapse state is local to the page session.
//
// 2. **Inline status pill** in the Status column. Clicking the
//    pill opens a small select menu that calls back into the
//    page-level kanban transition mutation (same code path the
//    board uses), with the same cancellation-reason flow for
//    moves to ``cancelled``.
//
// 3. **Inline assignee picker** in the Assignee column. Backed by
//    ``useMembersPickerQuery`` and the new ``assignCommitment``
//    endpoint.
//
// Every detail-link carries ``location.state`` with ``fromUrl`` so
// the back link on the Commitment detail page returns to the
// caller's exact filtered URL instead of the bare tracker route.

import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, ClockAlert, OctagonX, UserCircle2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Table } from "@/design-system";
import { AvatarCircle } from "@/design-system/components/AvatarCircle";

import { CommitmentPriorityBadge } from "./CommitmentPriorityBadge";
import { CommitmentStatusBadge, statusLabel } from "./CommitmentStatusBadge";
import {
  COMMITMENT_STATUSES_ORDERED,
  COMMITMENT_STATUS_COLUMN_META,
  DEFAULT_BOARD_STATUSES,
} from "./commitmentStatusMeta";
import { useCommitmentBucketInfiniteQuery } from "./hooks";
import { SearchableSelect } from "./SearchableSelect";
import type { MemberPickerRow } from "./api";
import type {
  CommitmentListFilters,
  CommitmentListRow,
  CommitmentSortKey,
  CommitmentStatus,
  CommitmentStatusCounts,
} from "./types";


/** Inputs the page hands the table when it wants per-group infinite
 * scroll instead of the legacy client-side grouping (which paged the
 * full filter set 25 rows at a time and split that page across all
 * buckets — a 4886-row tracker would show "Backlog 7 of 108"). When
 * supplied, every visible (non-collapsed) status group runs its own
 * paginated bucket query and lazy-loads more rows on scroll. */
export interface GroupedFetchInputs {
  filters: CommitmentListFilters;
  filterTree?: import("./urlState").FilterTree;
  sortBy: CommitmentSortKey;
  sortDir: "asc" | "desc";
}


export interface CommitmentListTableProps {
  rows: CommitmentListRow[];
  total: number;
  /** Per-status totals across the entire filtered set (independent
   * of the current page slice). When provided, the grouped header
   * count reflects the full filter; otherwise it falls back to the
   * page slice. */
  statusCounts?: CommitmentStatusCounts;
  sortBy: CommitmentSortKey;
  sortDir: "asc" | "desc";
  onSortChange: (next: {
    sortBy: CommitmentSortKey;
    sortDir: "asc" | "desc";
  }) => void;
  limit: number;
  offset: number;
  onOffsetChange: (next: number) => void;

  /** When true (default), rows render in collapsible groups by
   * status. Pass false to fall back to the original flat table. */
  groupByStatus?: boolean;
  /** When set together with ``groupByStatus``, switches each group
   * to a self-paginating infinite-scroll fetch (one BE query per
   * visible bucket). The ``rows`` prop is ignored in this mode —
   * each group owns its rows. The pager at the bottom is also
   * hidden because every bucket scrolls independently. */
  groupedFetch?: GroupedFetchInputs;
  /** Externally-controlled collapsed-group state, persisted by the
   * page (URL). Optional — when omitted the table keeps a local
   * Set so legacy non-routed renders still work. */
  collapsedStatuses?: CommitmentStatus[];
  onCollapseChange?: (next: CommitmentStatus[]) => void;
  /** Status set the caller wants to surface (mirrors the kanban
   * board). Defaults to the open 5 lanes; Cancelled is opt-in via
   * URL just like the board. */
  visibleStatuses?: CommitmentStatus[];
  /** Inline status pill change. The page wires this to the same
   * kanban transition mutation the board uses. */
  onMoveStatus?: (commitmentId: string, toStatus: CommitmentStatus) => void;
  /** Inline assignee change. ``null`` clears assignment. */
  onAssign?: (commitmentId: string, userId: string | null) => void;
  /** Inline due-date change. ``null`` clears the due date. */
  onSetDueDate?: (commitmentId: string, isoOrNull: string | null) => void;
  /** Members for the assignee picker. */
  members?: MemberPickerRow[];
  /** Id of the row currently in flight on a status/assignee
   * mutation; that row's controls disable to prevent double-fires. */
  pendingCommitmentId?: string | null;
  /** Stamp the detail link with router location state so the back
   * button can return to the original URL. */
  buildLinkState?: () => unknown;
}


interface ColumnConfig {
  key:
    | "title" | "account" | "status" | "priority" | "assigned"
    | "due_date_time" | "blocked" | "tags" | "updated_at";
  label: string;
  sortKey?: CommitmentSortKey;
  align?: "left" | "right" | "center";
  /** CSS width applied via ``<colgroup>`` so every per-status table
   *  shares the same column geometry — the tracker page renders one
   *  ``<table>`` per status group, so without this they drift. */
  width: string;
}


const COLUMNS: ColumnConfig[] = [
  { key: "title", label: "Commitment", sortKey: "title", align: "left", width: "26%" },
  { key: "account", label: "Account", align: "left", width: "14%" },
  { key: "status", label: "Status", sortKey: "status", align: "center", width: "12%" },
  { key: "priority", label: "Priority", sortKey: "priority", align: "center", width: "9%" },
  { key: "assigned", label: "Assignee", align: "center", width: "7%" },
  { key: "due_date_time", label: "Due", sortKey: "due_date_time", align: "left", width: "10%" },
  { key: "blocked", label: "Signals", align: "center", width: "6%" },
  { key: "tags", label: "Tags", align: "left", width: "8%" },
  { key: "updated_at", label: "Updated", sortKey: "updated_at", align: "left", width: "8%" },
];


function TableColgroup() {
  return (
    <colgroup>
      {COLUMNS.map((col) => (
        <col key={col.key} style={{ width: col.width }} />
      ))}
    </colgroup>
  );
}


/** Compact assignee chip — circular initials avatar with the full
 *  display name exposed via aria-label/title for hover discovery.
 *  Falls back to a neutral "no assignee" affordance when none is
 *  set, so the column never shrinks/expands per row. */
function AssigneeChip({ row }: { row: CommitmentListRow }) {
  const fullName =
    row.assigned_to_display_name ?? row.assigned_to_email ?? null;
  if (!fullName) {
    return (
      <span
        data-testid={`commitments-row-assignee-empty-${row.commitment_id}`}
        role="img"
        aria-label="Unassigned"
        title="Unassigned"
        className="inline-flex h-6 w-6 items-center justify-center text-text-tertiary"
      >
        <UserCircle2 size={18} aria-hidden="true" />
      </span>
    );
  }
  return (
    <AvatarCircle
      data-testid={`commitments-row-assignee-${row.commitment_id}`}
      size="md"
      name={fullName}
      title={fullName}
    />
  );
}


/** Tags cell — first chip + "+N" overflow indicator. The cell never
 *  widens to fit every tag; the full list is summarized into the
 *  cell's ``title`` for hover discovery. */
/** Compact signal icons for the Signals column. Order: Overdue
 * first, then Blocked, so the eye reads time-sensitive risk before
 * dependency state. Each chip is a 5×5 circle with a Lucide icon —
 * meaning lives in ``aria-label`` / ``title``, never visible text. */
function SignalsCell({
  row, overdue,
}: { row: CommitmentListRow; overdue: boolean }) {
  const id = row.commitment_id;
  if (!overdue && !row.is_blocked) {
    return <span className="text-text-tertiary">—</span>;
  }
  return (
    <span className="inline-flex items-center justify-center gap-1">
      {overdue && (
        <span
          data-testid={`commitments-overdue-${id}`}
          role="img"
          aria-label="Overdue"
          title={overdueTitle(row.due_date_time)}
          className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-ember-tint"
        >
          <ClockAlert size={12} aria-hidden="true" className="stroke-ember" />
        </span>
      )}
      {row.is_blocked && (
        <span
          data-testid={`commitments-blocked-${id}`}
          role="img"
          aria-label="Blocked"
          title={blockedTitle(row.blocked_reason)}
          className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-recessed text-blocked"
        >
          <OctagonX size={12} aria-hidden="true" />
        </span>
      )}
    </span>
  );
}


function overdueTitle(due: string | null): string {
  if (!due) return "Overdue";
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return "Overdue";
  return `Overdue since ${d.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  })}`;
}


function blockedTitle(reason: string | null): string {
  return reason ? `Blocked: ${reason}` : "Blocked";
}


function TagsCell({ row }: { row: CommitmentListRow }) {
  const id = row.commitment_id;
  if (row.tags.length === 0) {
    return (
      <span
        data-testid={`commitments-row-tags-${id}`}
        className="text-text-tertiary"
      >
        —
      </span>
    );
  }
  const [first, ...rest] = row.tags;
  const allNames = row.tags.map((t) => t.name).join(", ");
  return (
    <div
      data-testid={`commitments-row-tags-${id}`}
      title={allNames}
      className="flex min-w-0 items-center gap-1 overflow-hidden"
    >
      <span
        className={[
          "inline-flex min-w-0 max-w-full items-center truncate rounded-full px-2 py-0.5 text-[11px]",
          first.is_regulatory
            ? "bg-ember-tint text-ember-ink"
            : "bg-sage-tint text-teal",
        ].join(" ")}
      >
        {first.name}
      </span>
      {rest.length > 0 && (
        <span
          data-testid={`commitments-row-tags-overflow-${id}`}
          className="inline-flex shrink-0 items-center rounded-full bg-recessed px-1.5 py-0.5 text-[11px] text-text-secondary"
        >
          +{rest.length}
        </span>
      )}
    </div>
  );
}


export function CommitmentListTable(props: CommitmentListTableProps) {
  const {
    rows, total, statusCounts,
    sortBy, sortDir, onSortChange,
    limit, offset, onOffsetChange,
    groupByStatus = true,
    groupedFetch,
    collapsedStatuses,
    onCollapseChange,
    visibleStatuses,
    onMoveStatus, onAssign, onSetDueDate, members,
    pendingCommitmentId,
    buildLinkState,
  } = props;

  const showingFrom = rows.length === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + rows.length, total);
  const hasPrev = offset > 0;
  const hasNext = offset + rows.length < total;
  const columns = visibleStatuses ?? DEFAULT_BOARD_STATUSES;

  // Single ``<tr>`` of column headers, reused inside both the
  // legacy flat thead and the per-group sticky thead. We keep this
  // as a row (not a thead) so the parent can wrap it in a thead
  // with the right sticky classes for its context — wrapping a
  // thead in another thead is invalid and the browser drops the
  // inner one, which breaks ``<colgroup>`` cell sizing.
  const headRow = (
    <tr>
      {COLUMNS.map((col) => (
        <th
          key={col.key}
          scope="col"
          className={[
            "px-3 py-2 whitespace-nowrap",
            col.align === "right" ? "text-right" : "",
            col.align === "center" ? "text-center" : "",
          ].join(" ")}
          aria-sort={
            col.sortKey === sortBy
              ? sortDir === "asc" ? "ascending" : "descending"
              : col.sortKey ? "none" : undefined
          }
        >
          {col.sortKey ? (
            <SortHeaderButton
              label={col.label}
              active={col.sortKey === sortBy}
              sortDir={col.sortKey === sortBy ? sortDir : null}
              onClick={() =>
                onSortChange(
                  col.sortKey === sortBy
                    ? {
                        sortBy: col.sortKey,
                        sortDir: sortDir === "asc" ? "desc" : "asc",
                      }
                    : { sortBy: col.sortKey!, sortDir: "desc" },
                )
              }
            />
          ) : (
            col.label
          )}
        </th>
      ))}
    </tr>
  );

  // Wrap the row in the legacy non-sticky thead used by the
  // flat-pager body and the legacy client-side grouped body. The
  // per-group infinite-scroll path builds its own sticky thead
  // inside ``GroupSection`` so each section's column header pins
  // independently — wrapping a thead in another thead is invalid
  // and the browser drops the inner one (cells lose their
  // ``<colgroup>`` widths).
  const head = (
    <thead className="bg-recessed text-xs font-medium tracking-wide text-text-tertiary">
      {headRow}
    </thead>
  );

  return (
    <div data-testid="commitments-table" className="flex flex-col">
      {groupByStatus ? (
        <GroupedBody
          rows={rows}
          columns={columns}
          statusCounts={statusCounts}
          tableHead={head}
          tableHeadRow={headRow}
          groupedFetch={groupedFetch}
          collapsedStatuses={collapsedStatuses}
          onCollapseChange={onCollapseChange}
          onMoveStatus={onMoveStatus}
          onAssign={onAssign}
          onSetDueDate={onSetDueDate}
          members={members}
          pendingCommitmentId={pendingCommitmentId}
          buildLinkState={buildLinkState}
        />
      ) : (
        <div className="rounded-md border border-border-subtle">
          <Table scroll={false} layout="fixed">
            <TableColgroup />
            {head}
            <tbody>
              {rows.map((row) => (
                <CommitmentRow
                  key={row.commitment_id}
                  row={row}
                  onMoveStatus={onMoveStatus}
                  onAssign={onAssign}
                  onSetDueDate={onSetDueDate}
                  members={members}
                  pending={pendingCommitmentId === row.commitment_id}
                  buildLinkState={buildLinkState}
                />
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* The shared pager only makes sense in flat mode + the legacy
       * client-side grouped mode; per-group infinite scroll has its
       * own per-bucket "Load more" sentinel. */}
      {!groupedFetch && (
        <div className="mt-2 flex items-center justify-between text-xs text-text-tertiary">
          <span data-testid="commitments-pagination-summary">
            {total === 0
              ? "0 Commitments"
              : `Showing ${showingFrom}–${showingTo} of ${total}`}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="commitments-prev"
              disabled={!hasPrev}
              onClick={() => onOffsetChange(Math.max(0, offset - limit))}
              aria-label="Previous page"
              className="inline-flex h-7 items-center justify-center rounded-md px-2 text-text-secondary disabled:cursor-not-allowed disabled:text-text-tertiary hover:bg-recessed"
            >
              Prev
            </button>
            <button
              type="button"
              data-testid="commitments-next"
              disabled={!hasNext}
              onClick={() => onOffsetChange(offset + limit)}
              aria-label="Next page"
              className="inline-flex h-7 items-center justify-center rounded-md px-2 text-text-secondary disabled:cursor-not-allowed disabled:text-text-tertiary hover:bg-recessed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


interface GroupedBodyProps {
  rows: CommitmentListRow[];
  columns: CommitmentStatus[];
  statusCounts?: CommitmentStatusCounts;
  /** Pre-wrapped thead used by the legacy client-side grouped body
   * (one thead per section, non-sticky). */
  tableHead: React.ReactNode;
  /** Bare ``<tr>`` of column header cells used by the per-group
   * infinite-scroll path so each section can wrap it in its own
   * sticky thead without nesting. */
  tableHeadRow?: React.ReactNode;
  groupedFetch?: GroupedFetchInputs;
  collapsedStatuses?: CommitmentStatus[];
  onCollapseChange?: (next: CommitmentStatus[]) => void;
  onMoveStatus?: (commitmentId: string, toStatus: CommitmentStatus) => void;
  onAssign?: (commitmentId: string, userId: string | null) => void;
  onSetDueDate?: (commitmentId: string, isoOrNull: string | null) => void;
  members?: MemberPickerRow[];
  pendingCommitmentId?: string | null;
  buildLinkState?: () => unknown;
}


function GroupedBody({
  rows, columns, statusCounts, tableHead, tableHeadRow, groupedFetch,
  collapsedStatuses, onCollapseChange, onMoveStatus, onAssign,
  onSetDueDate, members, pendingCommitmentId, buildLinkState,
}: GroupedBodyProps) {
  const grouped = groupByStatus(rows, columns);
  // The collapsed set is either externally controlled (URL-driven
  // from the page) or kept as local state for legacy renders.
  const [localCollapsed, setLocalCollapsed] = useState<
    Set<CommitmentStatus>
  >(() => new Set());
  const collapsedControlled = collapsedStatuses !== undefined;
  const collapsed = collapsedControlled
    ? new Set(collapsedStatuses)
    : localCollapsed;

  function toggle(status: CommitmentStatus): void {
    if (collapsedControlled) {
      const set = new Set(collapsedStatuses);
      if (set.has(status)) set.delete(status);
      else set.add(status);
      // Preserve canonical board order for stable URLs.
      const ordered = COMMITMENT_STATUSES_ORDERED.filter(
        (s) => set.has(s),
      );
      onCollapseChange?.(ordered);
      return;
    }
    setLocalCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }

  // Per-group infinite-scroll path. Each visible status owns its
  // bucket query, so the surrounding ``rows`` prop is unused.
  if (groupedFetch) {
    return (
      <div
        data-testid="commitments-grouped-infinite"
        className="flex flex-col gap-3"
      >
        {columns.map((status) => (
          <GroupSection
            key={status}
            status={status}
            base={groupedFetch}
            tableHeadRow={tableHeadRow}
            totalForStatus={statusCounts?.[status] ?? 0}
            isCollapsed={collapsed.has(status)}
            onToggle={() => toggle(status)}
            onMoveStatus={onMoveStatus}
            onAssign={onAssign}
            onSetDueDate={onSetDueDate}
            members={members}
            pendingCommitmentId={pendingCommitmentId}
            buildLinkState={buildLinkState}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {columns.map((status) => {
        const meta = COMMITMENT_STATUS_COLUMN_META[status];
        const groupRows = grouped[status] ?? [];
        const isCollapsed = collapsed.has(status);
        return (
          <section
            key={status}
            data-testid={`commitments-group-${status}`}
            data-collapsed={isCollapsed ? "true" : "false"}
            className="rounded-md border border-border-subtle"
          >
            <button
              type="button"
              data-testid={`commitments-group-toggle-${status}`}
              onClick={() => toggle(status)}
              aria-expanded={!isCollapsed}
              className="flex w-full items-center gap-2 rounded-t-md bg-recessed/40 px-3 py-2 text-left text-sm font-semibold text-text-primary hover:bg-recessed"
            >
              {isCollapsed ? (
                <ChevronRight
                  size={14} aria-hidden="true" className="text-text-tertiary"
                />
              ) : (
                <ChevronDown
                  size={14} aria-hidden="true" className="text-text-tertiary"
                />
              )}
              <span
                aria-hidden="true"
                className={[
                  "inline-block h-1.5 w-1.5 rounded-full",
                  meta.accentDot,
                ].join(" ")}
              />
              <span>{meta.label}</span>
              <span
                data-testid={`commitments-group-count-${status}`}
                className="text-xs font-normal text-text-tertiary"
              >
                {statusCounts?.[status] ?? groupRows.length}
              </span>
            </button>
            {!isCollapsed && (
              <div>
                <Table scroll={false} layout="fixed">
                  <TableColgroup />
                  {tableHead}
                  <tbody>
                    {groupRows.length === 0 ? (
                      <tr
                        data-testid={`commitments-group-empty-${status}`}
                      >
                        <td
                          colSpan={COLUMNS.length}
                          className="px-3 py-4 text-center text-xs text-text-tertiary"
                        >
                          No Commitments in this status.
                        </td>
                      </tr>
                    ) : (
                      groupRows.map((row) => (
                        <CommitmentRow
                          key={row.commitment_id}
                          row={row}
                          onMoveStatus={onMoveStatus}
                          onAssign={onAssign}
                          onSetDueDate={onSetDueDate}
                          members={members}
                          pending={
                            pendingCommitmentId === row.commitment_id
                          }
                          buildLinkState={buildLinkState}
                        />
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}


/** Self-fetching grouped section: one bucket = one infinite-scroll
 * query. Used by ``CommitmentListTable`` when the caller supplies
 * ``groupedFetch``.
 *
 * Sticky behaviour: both the group header (status label + count +
 * chevron) and the column header row stick to the top of the
 * scrolling container, stacking the column header just under the
 * group header so the user always knows which bucket they're
 * scrolling within and which column each cell maps to. */
interface GroupSectionProps {
  status: CommitmentStatus;
  base: GroupedFetchInputs;
  /** Bare ``<tr>`` of column-header cells. ``GroupSection`` wraps
   * it in its own sticky ``<thead>`` so the column labels pin
   * within this section's scroll bounds. Wrapping a thead in
   * another thead would cause the browser to drop the inner one
   * and the column cells lose their ``<colgroup>`` widths. */
  tableHeadRow?: React.ReactNode;
  totalForStatus: number;
  isCollapsed: boolean;
  onToggle: () => void;
  onMoveStatus?: (commitmentId: string, toStatus: CommitmentStatus) => void;
  onAssign?: (commitmentId: string, userId: string | null) => void;
  onSetDueDate?: (commitmentId: string, isoOrNull: string | null) => void;
  members?: MemberPickerRow[];
  pendingCommitmentId?: string | null;
  buildLinkState?: () => unknown;
}


function GroupSection({
  status, base, tableHeadRow, totalForStatus, isCollapsed, onToggle,
  onMoveStatus, onAssign, onSetDueDate, members,
  pendingCommitmentId, buildLinkState,
}: GroupSectionProps) {
  const meta = COMMITMENT_STATUS_COLUMN_META[status];
  // ``include_cancelled`` lifts the BE's default cancelled filter so
  // the bucket actually returns rows when the user opens the
  // Cancelled lane explicitly.
  const baseFilters: CommitmentListFilters = {
    ...base.filters,
    ...(status === "cancelled" ? { include_cancelled: true } : {}),
  };
  const query = useCommitmentBucketInfiniteQuery({
    status,
    base: {
      filters: baseFilters,
      filterTree: base.filterTree,
      sortBy: base.sortBy,
      sortDir: base.sortDir,
    },
    // Skip fetching for collapsed groups + empty buckets — saves a
    // round-trip per render.
    enabled: !isCollapsed && totalForStatus > 0,
  });

  const rows: CommitmentListRow[] = (query.data?.pages ?? []).flatMap(
    (p) => p.rows,
  );

  // Auto-load the next page when the sentinel row enters the
  // viewport. IntersectionObserver is cheap and matches ClickUp's
  // scroll-driven behaviour; no manual "Load more" click needed.
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (isCollapsed) return;
    const node = sentinelRef.current;
    if (!node) return;
    if (!query.hasNextPage || query.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          query.fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    isCollapsed,
    query.hasNextPage,
    query.isFetchingNextPage,
    query.fetchNextPage,
    rows.length,
  ]);

  return (
    <section
      data-testid={`commitments-group-${status}`}
      data-collapsed={isCollapsed ? "true" : "false"}
      className="rounded-md border border-border-subtle"
    >
      <button
        type="button"
        data-testid={`commitments-group-toggle-${status}`}
        onClick={onToggle}
        aria-expanded={!isCollapsed}
        // Sticky group header. ``top-12`` (3rem ≈ 48px) parks it
        // below the page's sticky Commitments toolbar so the toolbar
        // stays visible. ``z-20`` keeps the header above the column
        // header row and any inline pickers stacked underneath.
        // ``bg-recessed`` (fully opaque) so scroll-through rows
        // don't bleed under the header text.
        className="sticky top-12 z-20 flex w-full items-center gap-2 rounded-t-md border-b border-border-subtle bg-recessed px-3 py-2 text-left text-sm font-semibold text-text-primary hover:bg-recessed"
      >
        {isCollapsed ? (
          <ChevronRight
            size={14} aria-hidden="true" className="text-text-tertiary"
          />
        ) : (
          <ChevronDown
            size={14} aria-hidden="true" className="text-text-tertiary"
          />
        )}
        <span
          aria-hidden="true"
          className={[
            "inline-block h-1.5 w-1.5 rounded-full",
            meta.accentDot,
          ].join(" ")}
        />
        <span>{meta.label}</span>
        <span
          data-testid={`commitments-group-count-${status}`}
          className="text-xs font-normal text-text-tertiary"
        >
          {totalForStatus}
        </span>
        {query.isFetching && !query.isFetchingNextPage && (
          <span
            data-testid={`commitments-group-loading-${status}`}
            className="ml-auto text-xs text-text-tertiary"
          >
            Loading…
          </span>
        )}
      </button>
      {!isCollapsed && (
        <div>
          <Table scroll={false} layout="fixed">
            <TableColgroup />
            {/* Sticky column header — pinned just below the page
             * toolbar (``top-12``) + group header (~36px) so the
             * column labels stay anchored to the current bucket as
             * the user scrolls. Wraps the bare ``<tr>`` of header
             * cells in a single ``<thead>`` so ``<colgroup>`` cell
             * widths apply correctly. Fully opaque ``bg-recessed``
             * so row text doesn't bleed through underneath. */}
            <thead
              className="sticky top-[5.25rem] z-10 bg-recessed text-xs font-medium tracking-wide text-text-tertiary"
            >
              {tableHeadRow}
            </thead>
            <tbody>
              {query.isLoading ? (
                <tr data-testid={`commitments-group-skeleton-${status}`}>
                  <td
                    colSpan={COLUMNS.length}
                    className="px-3 py-4 text-center text-xs text-text-tertiary"
                  >
                    Loading…
                  </td>
                </tr>
              ) : query.isError ? (
                <tr data-testid={`commitments-group-error-${status}`}>
                  <td
                    colSpan={COLUMNS.length}
                    className="px-3 py-4 text-center text-xs text-ember-ink"
                  >
                    Couldn't load this bucket.{" "}
                    <button
                      type="button"
                      onClick={() => query.refetch()}
                      className="underline"
                    >
                      Retry
                    </button>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr data-testid={`commitments-group-empty-${status}`}>
                  <td
                    colSpan={COLUMNS.length}
                    className="px-3 py-4 text-center text-xs text-text-tertiary"
                  >
                    No Commitments in this status.
                  </td>
                </tr>
              ) : (
                <>
                  {rows.map((row) => (
                    <CommitmentRow
                      key={row.commitment_id}
                      row={row}
                      onMoveStatus={onMoveStatus}
                      onAssign={onAssign}
                      onSetDueDate={onSetDueDate}
                      members={members}
                      pending={pendingCommitmentId === row.commitment_id}
                      buildLinkState={buildLinkState}
                    />
                  ))}
                  {/* Sentinel row drives IntersectionObserver-based
                   * lazy loading. ``hasNextPage`` guards against
                   * pointlessly observing after the bucket is
                   * fully drained. */}
                  {query.hasNextPage && (
                    <tr
                      ref={sentinelRef}
                      data-testid={`commitments-group-sentinel-${status}`}
                    >
                      <td
                        colSpan={COLUMNS.length}
                        className="px-3 py-2 text-center text-xs text-text-tertiary"
                      >
                        {query.isFetchingNextPage ? "Loading…" : ""}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </Table>
        </div>
      )}
    </section>
  );
}


function groupByStatus(
  rows: CommitmentListRow[], columns: CommitmentStatus[],
): Record<CommitmentStatus, CommitmentListRow[]> {
  const out = columns.reduce<
    Record<CommitmentStatus, CommitmentListRow[]>
  >((acc, status) => {
    acc[status] = [];
    return acc;
  }, {} as Record<CommitmentStatus, CommitmentListRow[]>);
  for (const row of rows) {
    if (!out[row.status]) continue;
    out[row.status].push(row);
  }
  return out;
}


interface RowProps {
  row: CommitmentListRow;
  onMoveStatus?: (commitmentId: string, toStatus: CommitmentStatus) => void;
  onAssign?: (commitmentId: string, userId: string | null) => void;
  onSetDueDate?: (commitmentId: string, isoOrNull: string | null) => void;
  members?: MemberPickerRow[];
  pending?: boolean;
  buildLinkState?: () => unknown;
}


function CommitmentRow({
  row, onMoveStatus, onAssign, onSetDueDate, members, pending, buildLinkState,
}: RowProps) {
  const overdue = isOverdue(row);
  const accountName = row.account_name ?? "Unknown account";
  const linkState = buildLinkState ? buildLinkState() : undefined;
  return (
    <tr
      data-testid={`commitments-row-${row.commitment_id}`}
      data-pending={pending ? "true" : "false"}
      className="border-t border-border-subtle"
    >
      <td className="px-3 py-2 text-text-primary">
        <div className="flex min-w-0 items-baseline gap-2">
          <Link
            to={`/dashboard/commitments/${row.commitment_id}`}
            state={linkState}
            data-testid={`commitments-detail-link-${row.commitment_id}`}
            title={row.title}
            className="min-w-0 flex-1 truncate font-medium hover:underline"
          >
            {row.title}
          </Link>
          <span className="shrink-0 text-xs text-text-tertiary">
            {row.commitment_number}
          </span>
        </div>
      </td>
      <td className="px-3 py-2 text-text-primary">
        <Link
          to={`/dashboard/accounts/${row.client_id}`}
          data-testid={`commitments-account-link-${row.commitment_id}`}
          title={accountName}
          className="block min-w-0 truncate hover:underline"
        >
          {accountName}
        </Link>
      </td>
      <td className="px-3 py-2 text-center">
        {onMoveStatus ? (
          <select
            data-testid={`commitments-row-status-select-${row.commitment_id}`}
            value={row.status}
            disabled={pending}
            onChange={(e) =>
              onMoveStatus(row.commitment_id, e.target.value as CommitmentStatus)
            }
            className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-xs text-text-primary disabled:cursor-not-allowed disabled:opacity-60"
          >
            {COMMITMENT_STATUSES_ORDERED.map((s) => (
              <option key={s} value={s}>{statusLabel(s)}</option>
            ))}
          </select>
        ) : (
          <CommitmentStatusBadge status={row.status} />
        )}
      </td>
      <td className="px-3 py-2 text-center">
        <CommitmentPriorityBadge priority={row.priority} />
      </td>
      <td className="px-3 py-2 text-text-secondary">
        <div className="flex items-center justify-center">
          {onAssign && members !== undefined ? (
            <SearchableSelect
              fieldId={`row-assignee-${row.commitment_id}`}
              value={row.assigned_to ?? ""}
              options={[
                { value: "", label: "Unassigned", isClear: true },
                ...members.map((m) => ({
                  value: m.id,
                  label: m.display_name || m.email,
                  hint: m.email,
                })),
              ]}
              triggerDisplay={
                <AssigneeChip row={row} />
              }
              disabled={pending}
              onPick={(userId) => onAssign(row.commitment_id, userId || null)}
            />
          ) : (
            <AssigneeChip row={row} />
          )}
        </div>
      </td>
      <td
        className={[
          "px-3 py-2 whitespace-nowrap",
          overdue ? "text-ember-ink font-medium" : "text-text-secondary",
        ].join(" ")}
      >
        {onSetDueDate ? (
          <DueDateEditor
            row={row}
            overdue={overdue}
            onSet={(iso) => onSetDueDate(row.commitment_id, iso)}
            disabled={pending}
          />
        ) : row.due_date_time ? (
          <span
            data-testid={`commitments-due-${row.commitment_id}`}
            title={dueTitle(row.due_date_time, overdue)}
          >
            {formatShortDate(row.due_date_time)}
          </span>
        ) : "—"}
      </td>
      <td className="px-3 py-2 text-center">
        <SignalsCell row={row} overdue={overdue} />
      </td>
      <td className="px-3 py-2">
        <TagsCell row={row} />
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-text-secondary">
        {row.updated_at ? (
          <span
            data-testid={`commitments-updated-${row.commitment_id}`}
            title={formatDate(row.updated_at)}
          >
            {formatShortDate(row.updated_at)}
          </span>
        ) : (
          <span data-testid={`commitments-updated-${row.commitment_id}`}>—</span>
        )}
      </td>
    </tr>
  );
}


function SortHeaderButton({
  label, active, sortDir, onClick,
}: {
  label: string;
  active: boolean;
  sortDir: "asc" | "desc" | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        active
          ? `Sort by ${label}, currently ${sortDir === "asc" ? "ascending" : "descending"}`
          : `Sort by ${label}`
      }
      className={[
        "inline-flex items-center gap-1 transition-fast ease-out",
        active ? "text-text-primary" : "hover:text-text-primary",
      ].join(" ")}
    >
      <span>{label}</span>
      {active && sortDir === "asc" ? (
        <ArrowUp size={10} aria-hidden="true" />
      ) : active && sortDir === "desc" ? (
        <ArrowDown size={10} aria-hidden="true" />
      ) : null}
    </button>
  );
}


function DueDateEditor({
  row, overdue, onSet, disabled,
}: {
  row: CommitmentListRow;
  overdue: boolean;
  onSet: (isoOrNull: string | null) => void;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const initialLocal = row.due_date_time
    ? toDatetimeLocal(row.due_date_time)
    : "";

  if (editing) {
    return (
      <input
        type="datetime-local"
        autoFocus
        data-testid={`commitments-due-input-${row.commitment_id}`}
        defaultValue={initialLocal}
        onBlur={(e) => {
          const next = e.target.value;
          setEditing(false);
          if (next === initialLocal) return;
          onSet(next ? new Date(next).toISOString() : null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            (e.target as HTMLInputElement).value = initialLocal;
            setEditing(false);
          }
          if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur();
          }
        }}
        disabled={disabled}
        className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-xs text-text-primary"
      />
    );
  }

  return (
    <button
      type="button"
      data-testid={`commitments-due-trigger-${row.commitment_id}`}
      disabled={disabled}
      onClick={() => setEditing(true)}
      className="rounded-md px-1 py-0.5 text-left hover:bg-recessed/60 disabled:cursor-not-allowed"
    >
      {row.due_date_time ? (
        <span
          data-testid={`commitments-due-${row.commitment_id}`}
          title={dueTitle(row.due_date_time, overdue)}
        >
          {formatShortDate(row.due_date_time)}
        </span>
      ) : (
        <span className="italic text-text-tertiary">Click to set</span>
      )}
    </button>
  );
}


function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}


function isOverdue(row: CommitmentListRow): boolean {
  if (!row.due_date_time) return false;
  if (row.status === "done" || row.status === "cancelled") return false;
  const due = new Date(row.due_date_time).getTime();
  if (Number.isNaN(due)) return false;
  return due < Date.now();
}


function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}


/** Compact tracker-row date: ``May 3``, no year. The full date
 *  rides the cell ``title`` for hover discovery. */
function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric",
  });
}


/** Hover text for the Due cell. Adds " · Overdue" when applicable so
 *  the marker is still discoverable even though it no longer
 *  consumes a column. */
function dueTitle(iso: string, overdue: boolean): string {
  const full = formatDate(iso);
  return overdue ? `${full} · Overdue` : full;
}
